import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { Card, Text, View } from "@src/components";
import type { WasteSenseMapSite } from "@visioni/a3s-reports/client-messages";
import { formatMapSiteHeading } from "../siteDisplay";

import "leaflet/dist/leaflet.css";

interface SiteLocationsMapProps {
  /** Marker entries; renders a placeholder card when empty. */
  sites: readonly WasteSenseMapSite[];
  /**
   * Currently-focused site. Drives marker styling (the focused marker
   * gets the larger crimson icon and `zIndexOffset: 1000`). Pure
   * presentation — does NOT trigger a map fly-to. Use `focusRequest`
   * for that.
   */
  focusSiteId?: string | null;
  /**
   * External fly-to request. Each new `key` value re-pans the map to
   * `siteId`, even if `siteId` matches the previous request. The
   * `key` is what lets consecutive "Locate on map" clicks on the
   * same row re-pan the map after the user has manually scrolled
   * away — a plain `siteId` prop would deduplicate and the second
   * click would be a no-op.
   */
  focusRequest?: { siteId: string; key: number } | null;
  /**
   * Invoked when a marker is clicked. The parent typically uses this
   * to open the matching site row in the list and mark it active.
   * Leaflet still opens the marker's popup independently; this
   * callback is purely for keeping the side list in sync.
   */
  onSelectSite?: (siteId: string) => void;
  /** Min map height in pixels. Defaults to 360 to match the side list. */
  minHeight?: number;
}

/**
 * Interactive Leaflet site map for the WasteSense report (web only).
 *
 * Mirrors the static report's behaviour:
 *  - One pin per `map_sites[]` entry (custom div pin, not Leaflet's PNG default)
 *  - Auto-fits the viewport to the marker bounds (or `setView` for a single marker)
 *  - Flies to a specific marker on every `focusRequest` (the `key` lets
 *    repeat clicks on the same hero row re-pan after the user has
 *    manually scrolled away)
 *  - Esri World Imagery base + Carto labels overlay (same tile sources)
 */
export function SiteLocationsMap({
  sites,
  focusSiteId,
  focusRequest,
  onSelectSite,
  minHeight = 360,
}: SiteLocationsMapProps) {
  const points = useMemo(() => sites.filter((s) => Number.isFinite(s.map_lat) && Number.isFinite(s.map_lon)), [sites]);

  const initialBounds = useMemo<L.LatLngBoundsLiteral | null>(() => {
    if (points.length < 2) return null;
    return points.map((p) => [p.map_lat, p.map_lon] as [number, number]);
  }, [points]);

  const initialCenter = useMemo<[number, number] | null>(() => {
    if (points.length !== 1) return null;
    const p = points[0]!;
    return [p.map_lat, p.map_lon];
  }, [points]);

  if (points.length === 0) {
    return (
      <Card
        flex={1}
        minHeight={minHeight}
        alignItems="center"
        justifyContent="center"
        bg="muted"
        borderColor="borderCard"
      >
        <Text textStyle="xs/semibold" color="textMuted" letterSpacing={1} textTransform="uppercase">
          No location data
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          This report did not include marker coordinates.
        </Text>
      </Card>
    );
  }

  // Common props for the two cases — single point uses centre+zoom, otherwise fitBounds.
  // `maxZoom` caps both the auto-fit AND user zoom so we never request tiles past
  // what Esri World_Imagery reliably serves (z18 globally; z19 only for major cities).
  // Without this, a one-pin tenant in regional NSW gets framed at z20 and the map
  // ends up showing only the grey "Map data not yet available" placeholder.
  const sharedMapProps = {
    scrollWheelZoom: true,
    maxZoom: ESRI_IMAGERY_MAX_ZOOM,
    style: { height: "100%", width: "100%" },
  } as const;

  return (
    <View
      flex={1}
      minHeight={minHeight}
      rounded={8}
      borderWidth={1}
      borderColor="borderCard"
      style={{ overflow: "hidden" }}
    >
      <MapContainer
        {...sharedMapProps}
        {...(initialBounds ?
          { bounds: initialBounds, boundsOptions: { padding: [32, 32], maxZoom: ESRI_IMAGERY_MAX_ZOOM } }
        : { center: initialCenter ?? [0, 0], zoom: SINGLE_POINT_ZOOM })}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution="Tiles &copy; Esri"
          maxZoom={ESRI_IMAGERY_MAX_ZOOM}
        />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
          subdomains={["a", "b", "c", "d"]}
          opacity={0.95}
          maxZoom={ESRI_IMAGERY_MAX_ZOOM}
        />

        {points.map((p, index) => {
          const isFocused = focusSiteId !== undefined && focusSiteId !== null && p.site_id === focusSiteId;
          return (
            <Marker
              key={p.site_id !== "" ? p.site_id : `index-fallback-${index}`}
              position={[p.map_lat, p.map_lon]}
              icon={isFocused ? activePinIcon : pinIcon}
              title={formatMapSiteHeading(p)}
              /**
               * Lift the focused marker above its neighbours when sites
               * cluster tightly (e.g. multiple sites on one street).
               * Without this the active marker can land underneath a
               * neighbour and the visual focus is hidden.
               */
              zIndexOffset={isFocused ? 1000 : 0}
              eventHandlers={onSelectSite ? { click: () => onSelectSite(p.site_id) } : undefined}
            >
              <Popup>
                <strong>{formatMapSiteHeading(p)}</strong>
                <br />
                <small>{p.confirmed_dumps.toLocaleString()} confirmed dumps</small>
              </Popup>
            </Marker>
          );
        })}

        <FocusController points={points} focusRequest={focusRequest ?? null} />
      </MapContainer>
    </View>
  );
}

/**
 * Maximum zoom level the Esri World_Imagery basemap reliably serves.
 *
 * Globally Esri publishes imagery to z18; only major metros reach z19 and
 * very few locations hit z20. Capping the map's `maxZoom` here keeps the
 * tile layer from requesting non-existent tiles, which Leaflet otherwise
 * renders as the grey "Map data not yet available" placeholder.
 */
const ESRI_IMAGERY_MAX_ZOOM = 18;

/**
 * Default zoom level when there's only one marker.
 *
 * z16 frames a city block — close enough to identify the property,
 * far enough that we always have imagery available.
 */
const SINGLE_POINT_ZOOM = 16;

/**
 * Re-centres the map whenever `focusRequest` changes — including the
 * "same site, new request" case where only the `key` bumps. Lives
 * inside the MapContainer so `useMap()` is in scope; the parent
 * component drives focus changes from outside via the prop.
 *
 * Tracking the last *request key* (not the assetId) is what fixes
 * repeat "Locate on map" clicks: the user clicks the same hero row
 * twice, the parent bumps `key`, and we re-pan even though the
 * assetId hasn't changed. Keying on assetId alone would short-circuit
 * the second click and leave the map wherever the user had scrolled.
 */
function FocusController({
  points,
  focusRequest,
}: {
  points: readonly WasteSenseMapSite[];
  focusRequest: { siteId: string; key: number } | null;
}) {
  const map = useMap();
  const lastRequestKey = useRef<number | null>(null);

  useEffect(() => {
    if (!focusRequest) return;
    if (focusRequest.key === lastRequestKey.current) return;
    const target = points.find((p) => p.site_id === focusRequest.siteId);
    if (!target) return;
    lastRequestKey.current = focusRequest.key;
    // Clamp between current zoom (don't zoom out) and the imagery cap
    // (don't zoom past where Esri serves tiles, otherwise we'd land on the grey
    // "Map data not yet available" placeholder for regional locations).
    const targetZoom = Math.min(Math.max(map.getZoom(), SINGLE_POINT_ZOOM - 1), ESRI_IMAGERY_MAX_ZOOM);
    map.flyTo([target.map_lat, target.map_lon], targetZoom, {
      duration: 0.6,
    });
  }, [focusRequest, map, points]);

  return null;
}

/**
 * Custom div-based pin so we don't depend on Leaflet's bundled PNG
 * markers (which require asset copies into the bundler output).
 *
 * Visual matches the static report: a rose-coloured dot.
 *
 * The dot is centred inside its icon box via flex so that the box's
 * geometric centre IS the dot's visual centre. That makes the
 * `iconAnchor` / `popupAnchor` arithmetic trivial — no compensating
 * for an inner `transform: translate(...)` offset, which used to put
 * the popup tip several pixels off the actual marker position.
 */
const PIN_BOX = 24;

const pinIcon = L.divIcon({
  className: "wastesense-map-pin",
  html: `<span style="
    display:flex;
    align-items:center;
    justify-content:center;
    width:${PIN_BOX}px;
    height:${PIN_BOX}px;
  "><span style="
    display:block;
    width:14px;
    height:14px;
    border-radius:50%;
    background:#e8456b;
    box-shadow:0 0 0 3px rgba(232,69,107,0.25), 0 1px 2px rgba(0,0,0,0.4);
  "></span></span>`,
  iconSize: [PIN_BOX, PIN_BOX],
  iconAnchor: [PIN_BOX / 2, PIN_BOX / 2],
  popupAnchor: [0, -(PIN_BOX / 2) - 2],
});

/**
 * Focused-marker variant. Larger dot, deeper red, white ring + wider
 * glow so the active site is unambiguous among a cluster of default
 * pins. The colour shift (rose → crimson) does the heavy lifting for
 * users with reduced colour sensitivity; the size delta backs it up
 * so it remains legible on grayscale prints.
 *
 * Same flex-centred geometry as the default pin so the dot's centre
 * sits at the same lat/lon when focus moves between markers — no
 * jitter as the icon swaps in.
 */
const ACTIVE_PIN_BOX = 32;

const activePinIcon = L.divIcon({
  className: "wastesense-map-pin wastesense-map-pin--active",
  html: `<span style="
    display:flex;
    align-items:center;
    justify-content:center;
    width:${ACTIVE_PIN_BOX}px;
    height:${ACTIVE_PIN_BOX}px;
  "><span style="
    display:block;
    width:20px;
    height:20px;
    border-radius:50%;
    background:#c4304f;
    border:2px solid #ffffff;
    box-shadow:0 0 0 5px rgba(196,48,79,0.30), 0 2px 6px rgba(0,0,0,0.5);
  "></span></span>`,
  iconSize: [ACTIVE_PIN_BOX, ACTIVE_PIN_BOX],
  iconAnchor: [ACTIVE_PIN_BOX / 2, ACTIVE_PIN_BOX / 2],
  popupAnchor: [0, -(ACTIVE_PIN_BOX / 2) - 2],
});
